<template>
	<div class="popup search-popup" style="width:1000px;" v-click-outside="() => $emit('closePop')">
		<div class="popup-top">
			<strong>{{ lang.label('facilitySearch') }}</strong>
			<button class="btn-close" @click="handleClosePop"></button>
		</div>
		<div class="popup-btm">
			<div class="popup-search">
				<div class="in">
					<v-select
						v-model="sltModel"
						:items="sltList"
						item-title="label"
						item-value="value"
						variant="outlined"
						style="max-width:136px"
						class="custom-select form"
					>
					</v-select>

					<!-- 자동완성 텍스트필드 -->
					<v-autocomplete
						v-model="searchText"
						:items="allFacilityList"
						:item-title="item => sltModel === 'facilityName' ? item.facilityName : item.facilityAddress"
						:item-value="item => sltModel === 'facilityName' ? item.facilityName : item.facilityAddress"
						class="custom-input form"
						style="width:380px;"
						:placeholder="lang.placeholder('searchKeyword')"
						:hide-details="true"
						:search-input.sync="searchText"
						:no-data-text="facilityNoSearchDataText"
						menu-icon=""
						:return-object="false"
						ref="autoCompleteRef"
						base-color="#fff"
						@keydown.enter="handleEnter"
					>
						<!-- #item 슬롯 구조는 그대로 유지 -->
						<template #item="{ props }">
							<v-list-item v-bind="props"/>
						</template>
					</v-autocomplete>

					<!-- 검색 / 초기화 버튼 -->
					<div class="search-btn">
						<v-btn class="custom-btn btn-white mini" @click="handleReset">{{ lang.button('reset') }}</v-btn>
						<v-btn class="custom-btn btn-blue mini" @click="handleSearch">{{ lang.button('search') }}</v-btn>
					</div>
				</div>
			</div>
			<div class="popup-search-cont">
				<div class="in">
					<div class="search-result-tit">
						<strong class="view-box-tit">{{ lang.label('searchResults') }}<span>{{ totalElements }}</span></strong>
						<div class="search-btn-wrap">
<!--							<v-select-->
<!--								v-model="pageSize"-->
<!--								:items="pageSizeGroup"-->
<!--								item-title="text"-->
<!--								item-value="value"-->
<!--								variant="outlined"-->
<!--								style="width:136px"-->
<!--								class="custom-select form"-->
<!--								:hide-details="true"-->
<!--							>-->
<!--							</v-select>-->
						</div>
					</div>
					<v-data-table
						:headers="facilityTableHeader"
						:items="facilityTableData.content"
						:items-per-page="Number(pageSize)"
						item-value="id"
						hide-default-footer
						class="custom-table"
						@update:page="setPage"
					>
						<template v-slot:item="{ item }: { item: FacilityData }">
							<tr
								@click="handleRowClick(item)"
								:class="{ 'highlight-row': item.id === facility?.id }"
							>
								<!-- 헤더 순서에 맞게 td를 반복 렌더링 -->
								<td v-for="header in facilityTableHeader" :key="header.key">
									{{ (item as FacilityData)[header.key as keyof FacilityData] }}
								</td>
							</tr>
						</template>
						<template v-slot:no-data>
							<p class="td-not-data">{{ lang.label('noData') }}</p>
						</template>
					</v-data-table>
					<div class="paging-wrap">
						<pagination pagination-key="useFacility"></pagination>
<!--						<div class="paging-btn">-->
<!--							<v-btn class="custom-btn btn-white mini" @click="$emit('closePop')">취소</v-btn>-->
<!--							<v-btn class="custom-btn btn-blue mini">확인</v-btn>-->
<!--						</div>-->
					</div>
				</div>
			</div>
		</div>
		<div class="popup-btn web-hide">
			<v-btn class="custom-btn btn-blue" @click="$emit('closePop')">{{ lang.button('apply') }}</v-btn>
		</div>
	</div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch, computed, nextTick} from 'vue'
import type { FacilityData } from '~/types/facility';
import pagination from '../components/pagination.vue'
import {useAuth} from "~/composables/useAuth";
import {useHandleCode} from "~/composables/useHandleCode";
import {useFacility} from "~/composables/useFacility";
import {usePagination} from "~/composables/usePagination";
import { useLanguage } from '~/composables/useLanguage';

const { handleCode } = useHandleCode();

const { lang } = useLanguage();

const mainCommonCodeGroup = 'common';
const facilitySearchCodeGroup = 'facilitySearch';
const faciltyNoSearchDataCodeGroup = 'facilityNoSearchData';

const sltList = ref([
		{value: 'facilityName', label: handleCode(mainCommonCodeGroup, facilitySearchCodeGroup, "facilityName")},
		{value: 'facilityAddress', label: handleCode(mainCommonCodeGroup, facilitySearchCodeGroup, "facilityAddress")}
	]
)

// 선택된 검색 항목
const sltModel = ref('facilityName');
// 검색어
const searchText = ref(null);
// 검색어 입력시 반응형 데이터
const autoCompleteRef = ref();
// 검색어에 따라 자동완성 리스트를 보여줄지 여부
const menuVisible = ref(false);
// 연속 엔터 입력 방지
const isManuallySelected = ref(false);

// closePop 이벤트를 emit 하기 위해 defineEmits 사용
// 장비관리(terminal_.vue) 페이지 내에 선택한 값 input에 넣기 위한 emit 추가
const emit = defineEmits(['closePop', 'selectFacilityName'])

const {
	pageSize,
	currentPage,
	totalElements,
	setPage
} = usePagination('useFacility');

const {
	allFacilityList,
	facility,
	facilityTableHeader,
	facilityTableData,
	handleFacilityAllData,
	handleFacilityTableData,
	handleFacilitySelect,
	handleFacilityPageIndex
} = useFacility();

// 팝업창 닫기
const handleClosePop = async () => {
	if (facility.value) {
		const index = await handleFacilityPageIndex(facility.value.id);

		if (index >= 0) {
			currentPage.value = Math.floor(index / Number(pageSize.value)) + 1;
		} else {
			currentPage.value = 1; // 기본값 설정
		}
	} else {
		currentPage.value = 1; // facility가 없는 경우 기본값 설정
	}

	emit('closePop')
}

// 선택한 row의 id를 sessionStorage에 저장
const handleRowClick = (item: FacilityData) => {
	handleFacilitySelect(item);

	emit('selectFacilityName', {
		id: item.id,
		name: item.facilityName
	})

	emit('closePop');
};

// user 권한
const { isAdmin } = useAuth()

// 검색하는 데이터가 없을 경우
const facilityNoSearchDataText = computed(() =>
	sltModel.value === 'facilityName'
		? `${handleCode(mainCommonCodeGroup, faciltyNoSearchDataCodeGroup, "facilityName")}`
		: `${handleCode(mainCommonCodeGroup, faciltyNoSearchDataCodeGroup, "facilityAddress")}`
)

// 초기화
const handleReset = () => {
	searchText.value = null;
}

// 검색
const handleSearch = async () => {
	await handleFacilityTableData({
		page: 0,
		size: Number(pageSize.value),
		facilityName: sltModel.value === 'facilityName' ? searchText.value : null,
		facilityAddress: sltModel.value === 'facilityAddress' ? searchText.value : null
	});
}

// 엔터키로 리스트 첫번째 선택
const handleEnter = () => {
	const items = autoCompleteRef.value.filteredItems;

	if (items.length === 1 && !isManuallySelected.value) {
		const firstItem = items[0];
		const value = firstItem.value;

		searchText.value = value;
		autoCompleteRef.value.search = value;

		isManuallySelected.value = true; // 이후 엔터는 무시

		nextTick(() => {
			autoCompleteRef.value.menu = false;
		});
	}
};

onMounted(async () => {
	if(!isAdmin) {
		if(facility.value) {
			const index = await handleFacilityPageIndex(facility.value.id);
			if (index >= 0) {
				currentPage.value = Math.floor(index / Number(pageSize.value));
			} else {
				currentPage.value = 1; // 기본값 설정
			}
		}
	}

	await handleFacilityAllData();
	await handleFacilityTableData(); // currentPage가 정해진 후 호출
});

// 메뉴 열기 동기화
watch(menuVisible, (val) => {
	if (val) autoCompleteRef.value.menu = true
})

// 선택 후 자동 메뉴 닫기
watch(searchText, () => {
	menuVisible.value = false;
	isManuallySelected.value = false;
})
</script>
